import { useState, useMemo, useEffect } from 'react'
import { useDepartment } from '../../context/DepartmentContext'
import { useAuth } from '../../context/AuthContext'
import {
  getDailyPnL, getPeriodPnL, getCashFlowSummary, prepareVAT201, prepareIRP6,
  getDataCompletenessScore, getGLAccounts, extractVAT, getStockValuation,
  getBreakevenAnalysis, getLockedPeriods, lockPeriod, unlockPeriod, isPeriodLocked,
  refreshPeriodLocks, refreshAuditTrail,
  getAuditTrail, CHART_OF_ACCOUNTS, VAT_RATE, TRADING_DAYS_PER_MONTH
} from '../../lib/accounting'
import { getPosSales, getOverheads, getAllPosProducts } from '../../lib/posStore'
import {
  DollarSign, FileText, TrendingUp, TrendingDown, Lock, Unlock,
  Download, Calendar, BarChart3, Shield, AlertTriangle, CheckCircle,
  BookOpen, Receipt, Calculator, PieChart
} from 'lucide-react'

// ── Helpers ──

function fmt(n) { return `R ${(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` }
function fmtPct(n) { return `${(n || 0).toFixed(1)}%` }
function fmtInt(n) { return (n || 0).toLocaleString('en-ZA') }

const TABS = [
  { id: 'dailyPnl', label: 'Daily P&L', icon: Calendar },
  { id: 'periodPnl', label: 'Period P&L', icon: FileText },
  { id: 'cashFlow', label: 'Cash Flow', icon: BarChart3 },
  { id: 'tax', label: 'Tax Compliance', icon: Shield },
  { id: 'coa', label: 'Chart of Accounts', icon: BookOpen },
]

const PERIOD_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: '7days', label: '7 Days' },
  { id: '30days', label: '30 Days' },
  { id: 'custom', label: 'Custom' },
]

function getDateRange(preset, customFrom, customTo) {
  const today = new Date()
  const todayStr = today.toISOString().split('T')[0]
  if (preset === 'custom' && customFrom && customTo) return { from: customFrom, to: customTo }
  if (preset === 'today') return { from: todayStr, to: todayStr }
  if (preset === '7days') {
    const d = new Date(today); d.setDate(d.getDate() - 7)
    return { from: d.toISOString().split('T')[0], to: todayStr }
  }
  if (preset === '30days') {
    const d = new Date(today); d.setDate(d.getDate() - 30)
    return { from: d.toISOString().split('T')[0], to: todayStr }
  }
  return { from: todayStr, to: todayStr }
}

function getVATPeriods() {
  const year = new Date().getFullYear()
  const periods = []
  for (let y = year; y >= year - 1; y--) {
    for (let m = 0; m < 12; m += 2) {
      const from = `${y}-${String(m + 1).padStart(2, '0')}-01`
      const endMonth = m + 2
      const endDate = new Date(y, endMonth, 0)
      const to = endDate.toISOString().split('T')[0]
      const label = `${new Date(y, m).toLocaleString('en-ZA', { month: 'short' })} – ${new Date(y, m + 1).toLocaleString('en-ZA', { month: 'short' })} ${y}`
      periods.push({ from, to, label })
    }
  }
  return periods
}

function getTaxYears() {
  const year = new Date().getFullYear()
  return [
    { from: `${year}-03-01`, to: `${year + 1}-02-28`, label: `${year}/${year + 1}` },
    { from: `${year - 1}-03-01`, to: `${year}-02-28`, label: `${year - 1}/${year}` },
    { from: `${year - 2}-03-01`, to: `${year - 1}-02-28`, label: `${year - 2}/${year - 1}` },
  ]
}

// ── Grade Badge ──

function GradeBadge({ grade, pct }) {
  const colors = {
    A: 'bg-green-900/40 text-green-400 border-green-800',
    B: 'bg-blue-900/40 text-blue-400 border-blue-800',
    C: 'bg-amber-900/40 text-amber-400 border-amber-800',
    D: 'bg-red-900/40 text-red-400 border-red-800',
  }
  return (
    <span className={`text-[11px] font-semibold px-2 py-0.5 border ${colors[grade] || colors.D}`}>
      Grade {grade} — {pct}%
    </span>
  )
}

// ── Profit indicator ──

function ProfitIndicator({ value, label }) {
  const positive = value >= 0
  return (
    <div className="flex items-center gap-1.5">
      {positive
        ? <TrendingUp size={14} className="text-green-400" />
        : <TrendingDown size={14} className="text-red-400" />
      }
      <span className={positive ? 'text-green-400' : 'text-red-400'}>
        {fmt(value)}
      </span>
      {label && <span className="text-gray-500 text-xs ml-1">{label}</span>}
    </div>
  )
}

// ── CSV Export ──

function exportCSV(filename, headers, rows) {
  const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.click()
  URL.revokeObjectURL(url)
}

// ── Bar visualization ──

function SimpleBar({ label, value, maxValue, color = 'bg-brand-600' }) {
  const pct = maxValue > 0 ? Math.min((Math.abs(value) / maxValue) * 100, 100) : 0
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-gray-400">{label}</span>
        <span className="text-white font-medium">{fmt(value)}</span>
      </div>
      <div className="h-3 bg-gray-800 w-full">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════

export default function Accounting() {
  const { currentDepartmentId } = useDepartment()
  const { user } = useAuth()
  const [tab, setTab] = useState('dailyPnl')

  // Daily P&L state
  const today = new Date().toISOString().split('T')[0]
  const [dailyDate, setDailyDate] = useState(today)

  // Period P&L state
  const [periodPreset, setPeriodPreset] = useState('today')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')

  // Cash Flow state
  const [cfPreset, setCfPreset] = useState('30days')
  const [cfCustomFrom, setCfCustomFrom] = useState('')
  const [cfCustomTo, setCfCustomTo] = useState('')

  // Tax state
  const vatPeriods = useMemo(() => getVATPeriods(), [])
  const taxYears = useMemo(() => getTaxYears(), [])
  const [selectedVATPeriod, setSelectedVATPeriod] = useState(0)
  const [selectedTaxYear, setSelectedTaxYear] = useState(0)
  const [taxSubTab, setTaxSubTab] = useState('vat')

  // Lock refresh key
  const [lockKey, setLockKey] = useState(0)

  // Pull latest period locks + audit trail from Firestore on mount
  // + when the department changes. A lock placed on Device A prevents
  // edits on Device B. Audit trail from Device A appears on Device B.
  // Both calls are non-blocking — fall back silently to local cache.
  useEffect(() => {
    if (!currentDepartmentId) return
    let cancelled = false
    ;(async () => {
      await Promise.all([
        refreshPeriodLocks(currentDepartmentId),
        refreshAuditTrail(currentDepartmentId),
      ])
      if (!cancelled) setLockKey(k => k + 1)
    })()
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDepartmentId])

  // ── Computed data ──

  const dailyPnl = useMemo(
    () => getDailyPnL(currentDepartmentId, dailyDate),
    [currentDepartmentId, dailyDate, lockKey]
  )

  const periodRange = getDateRange(periodPreset, customFrom, customTo)
  const periodPnl = useMemo(
    () => getPeriodPnL(currentDepartmentId, periodRange.from, periodRange.to),
    [currentDepartmentId, periodRange.from, periodRange.to]
  )

  const cfRange = getDateRange(cfPreset, cfCustomFrom, cfCustomTo)
  const cashFlow = useMemo(
    () => getCashFlowSummary(currentDepartmentId, cfRange.from, cfRange.to),
    [currentDepartmentId, cfRange.from, cfRange.to]
  )

  const vatData = useMemo(() => {
    const p = vatPeriods[selectedVATPeriod]
    return p ? prepareVAT201(currentDepartmentId, p.from, p.to) : null
  }, [currentDepartmentId, selectedVATPeriod, vatPeriods])

  const irp6Data = useMemo(() => {
    const y = taxYears[selectedTaxYear]
    return y ? prepareIRP6(currentDepartmentId, y.from, y.to) : null
  }, [currentDepartmentId, selectedTaxYear, taxYears])

  const completeness = useMemo(
    () => getDataCompletenessScore(currentDepartmentId, today),
    [currentDepartmentId, today]
  )

  const breakeven = useMemo(
    () => getBreakevenAnalysis(currentDepartmentId),
    [currentDepartmentId]
  )

  const stockVal = useMemo(
    () => getStockValuation(currentDepartmentId),
    [currentDepartmentId]
  )

  const auditTrail = useMemo(
    () => getAuditTrail(currentDepartmentId, null, null, 20),
    [currentDepartmentId, lockKey]
  )

  const glAccounts = useMemo(() => getGLAccounts(), [])

  const dayLocked = useMemo(
    () => isPeriodLocked(currentDepartmentId, dailyDate),
    [currentDepartmentId, dailyDate, lockKey]
  )

  // ── Handlers ──

  function handleLockDay() {
    if (dayLocked) {
      unlockPeriod(currentDepartmentId, dailyDate, user?.name || 'unknown')
    } else {
      lockPeriod(currentDepartmentId, dailyDate, user?.name || 'unknown')
    }
    setLockKey(k => k + 1)
  }

  function handleExportPeriodPnl() {
    const p = periodPnl
    const headers = ['Line Item', 'Amount (R)']
    const rows = [
      ['Revenue (incl VAT)', p.revenue.total.toFixed(2)],
      ['Revenue (excl VAT)', p.revenue.exclusive.toFixed(2)],
      ['Output VAT', p.revenue.outputVAT.toFixed(2)],
      ['', ''],
      ['Cost of Goods Sold', p.costOfSales.cogs.toFixed(2)],
      ['Wastage & Spoilage', p.costOfSales.wastage.toFixed(2)],
      ['Total Cost of Sales', p.costOfSales.total.toFixed(2)],
      ['', ''],
      ['Gross Profit', p.grossProfit.toFixed(2)],
      ['Gross Margin %', p.grossMargin.toFixed(1)],
      ['', ''],
      ...p.overheads.byItem.map(o => [`Overhead: ${o.name}`, ((o.amount / TRADING_DAYS_PER_MONTH) * p.period.tradingDays).toFixed(2)]),
      ['Total Overheads (period)', p.overheads.period.toFixed(2)],
      ['', ''],
      ['Net Profit', p.netProfit.toFixed(2)],
      ['Net Margin %', p.netMargin.toFixed(1)],
      ['', ''],
      ['VAT Output', p.vatReturn.outputVAT.toFixed(2)],
      ['VAT Input', p.vatReturn.inputVAT.toFixed(2)],
      ['VAT Net Payable', p.vatReturn.netPayable.toFixed(2)],
    ]
    exportCSV(`pnl_${periodRange.from}_${periodRange.to}.csv`, headers, rows)
  }

  function handleExportVAT() {
    if (!vatData) return
    const headers = ['Box', 'Description', 'Amount (R)']
    const rows = [
      ['Box 1', 'Standard Rated Supplies (excl VAT)', vatData.box1_standardRatedSupplies.toFixed(2)],
      ['Box 1A', 'Output VAT on Supplies', vatData.box1a_outputVAT.toFixed(2)],
      ['Box 14', 'Capital Goods', vatData.box14_capitalGoods.toFixed(2)],
      ['Box 15', 'Input VAT on Acquisitions', vatData.box15_inputVAT.toFixed(2)],
      ['Box 18', 'Net VAT Payable / (Refundable)', vatData.box18_netVAT.toFixed(2)],
    ]
    const p = vatPeriods[selectedVATPeriod]
    exportCSV(`vat201_${p.from}_${p.to}.csv`, headers, rows)
  }

  // ═══════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">

        {/* ── Page Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Calculator size={24} className="text-brand-500" />
              Accounting & Tax Compliance
            </h1>
            <p className="text-gray-400 text-sm mt-1">Financial management, VAT returns & SARS compliance</p>
          </div>
        </div>

        {/* ── Top Indicators ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Data Completeness */}
          <div className="bg-gray-900 border border-gray-800 p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">Data Completeness</span>
              <GradeBadge grade={completeness.grade} pct={completeness.pct} />
            </div>
            <div className="h-2 bg-gray-800 w-full">
              <div
                className={`h-full transition-all ${completeness.pct >= 80 ? 'bg-green-500' : completeness.pct >= 60 ? 'bg-amber-500' : 'bg-red-500'}`}
                style={{ width: `${completeness.pct}%` }}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {completeness.checks.map((c, i) => (
                <span key={i} className={`text-[10px] px-1.5 py-0.5 border ${c.ok ? 'border-green-800 text-green-400' : 'border-red-800 text-red-400'}`}>
                  {c.ok ? <CheckCircle size={10} className="inline mr-0.5 -mt-0.5" /> : <AlertTriangle size={10} className="inline mr-0.5 -mt-0.5" />}
                  {c.name}
                </span>
              ))}
            </div>
          </div>

          {/* Breakeven */}
          <div className="bg-gray-900 border border-gray-800 p-4 space-y-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">Breakeven Analysis</span>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Daily target</span>
                <span className="text-white font-medium">{fmt(breakeven.breakeven.daily)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Monthly target</span>
                <span className="text-white font-medium">{fmt(breakeven.breakeven.monthly)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Avg CM ratio</span>
                <span className="text-brand-400 font-medium">{fmtPct(breakeven.avgContributionMarginRatio)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Fixed costs / day</span>
                <span className="text-white font-medium">{fmt(breakeven.fixedCosts.daily)}</span>
              </div>
            </div>
          </div>

          {/* Stock Valuation */}
          <div className="bg-gray-900 border border-gray-800 p-4 space-y-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">Stock Valuation</span>
            <div className="text-2xl font-bold text-white">{fmt(stockVal.totalValue)}</div>
            <div className="text-xs text-gray-500">{stockVal.items.length} items — {stockVal.method.replace('_', ' ')} method</div>
            <div className="space-y-0.5 max-h-20 overflow-y-auto">
              {stockVal.items.slice(0, 5).map((item, i) => (
                <div key={i} className="flex justify-between text-[11px]">
                  <span className="text-gray-400 truncate mr-2">{item.name}</span>
                  <span className="text-gray-300 whitespace-nowrap">{fmt(item.totalValue)}</span>
                </div>
              ))}
              {stockVal.items.length > 5 && (
                <span className="text-[10px] text-gray-600">+{stockVal.items.length - 5} more</span>
              )}
            </div>
          </div>
        </div>

        {/* ── Tab Navigation ── */}
        <div className="flex gap-1 border-b border-gray-800 overflow-x-auto">
          {TABS.map(t => {
            const Icon = t.icon
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                  tab === t.id
                    ? 'border-brand-500 text-brand-400'
                    : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'
                }`}
              >
                <Icon size={15} />
                {t.label}
              </button>
            )
          })}
        </div>

        {/* ══════════════════════════════════════ */}
        {/* TAB 1: DAILY P&L                      */}
        {/* ══════════════════════════════════════ */}
        {tab === 'dailyPnl' && (
          <div className="space-y-4">
            {/* Controls */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Calendar size={16} className="text-gray-400" />
                <input
                  type="date"
                  value={dailyDate}
                  onChange={e => setDailyDate(e.target.value)}
                  className="bg-gray-900 border border-gray-700 text-white text-sm px-3 py-1.5 focus:outline-none focus:border-brand-500"
                />
              </div>
              <button
                onClick={handleLockDay}
                className={`flex items-center gap-1.5 text-sm px-3 py-1.5 border transition-colors ${
                  dayLocked
                    ? 'bg-amber-900/30 border-amber-700 text-amber-400 hover:bg-amber-900/50'
                    : 'bg-gray-900 border-gray-700 text-gray-300 hover:border-brand-500 hover:text-brand-400'
                }`}
              >
                {dayLocked ? <Lock size={14} /> : <Unlock size={14} />}
                {dayLocked ? 'Unlock Day' : 'Lock Day'}
              </button>
              {dayLocked && (
                <span className="text-[11px] text-amber-400 flex items-center gap-1">
                  <Lock size={12} /> Period locked — data is read-only
                </span>
              )}
            </div>

            {/* Revenue & Profit Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gray-900 border border-gray-800 p-4">
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Revenue (incl VAT)</div>
                <div className="text-xl font-bold">{fmt(dailyPnl.revenue.inclusive)}</div>
                <div className="text-xs text-gray-500 mt-1">Excl VAT: {fmt(dailyPnl.revenue.exclusive)}</div>
              </div>
              <div className="bg-gray-900 border border-gray-800 p-4">
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Cost of Goods Sold</div>
                <div className="text-xl font-bold">{fmt(dailyPnl.cogs.total)}</div>
                <div className="text-xs text-gray-500 mt-1">Wastage: {fmt(dailyPnl.cogs.wastage)}</div>
              </div>
              <div className="bg-gray-900 border border-gray-800 p-4">
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Gross Profit</div>
                <ProfitIndicator value={dailyPnl.grossProfit} />
                <div className="text-xs text-gray-500 mt-1">Margin: {fmtPct(dailyPnl.grossMargin)}</div>
              </div>
              <div className="bg-gray-900 border border-gray-800 p-4">
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Net Profit</div>
                <ProfitIndicator value={dailyPnl.netProfit} />
                <div className="text-xs text-gray-500 mt-1">Margin: {fmtPct(dailyPnl.netMargin)}</div>
              </div>
            </div>

            {/* Detail Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Left: Income Statement */}
              <div className="lg:col-span-2 bg-gray-900 border border-gray-800 p-5 space-y-3">
                <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider flex items-center gap-2">
                  <Receipt size={15} className="text-brand-500" />
                  Daily Income Statement
                </h3>
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="border-b border-gray-800">
                      <td className="py-1.5 text-gray-400">Revenue (incl VAT)</td>
                      <td className="py-1.5 text-right font-medium">{fmt(dailyPnl.revenue.inclusive)}</td>
                    </tr>
                    <tr className="border-b border-gray-800">
                      <td className="py-1.5 text-gray-400 pl-4">Less: VAT Output</td>
                      <td className="py-1.5 text-right text-gray-400">({fmt(dailyPnl.revenue.outputVAT)})</td>
                    </tr>
                    <tr className="border-b border-gray-800 bg-gray-800/30">
                      <td className="py-1.5 text-gray-200 font-medium">Revenue (excl VAT)</td>
                      <td className="py-1.5 text-right font-medium">{fmt(dailyPnl.revenue.exclusive)}</td>
                    </tr>
                    <tr className="border-b border-gray-800">
                      <td className="py-1.5 text-gray-400 pl-4">Less: Cost of Goods Sold</td>
                      <td className="py-1.5 text-right text-gray-400">({fmt(dailyPnl.cogs.total)})</td>
                    </tr>
                    <tr className="border-b border-gray-800">
                      <td className="py-1.5 text-gray-400 pl-4">Less: Wastage</td>
                      <td className="py-1.5 text-right text-gray-400">({fmt(dailyPnl.cogs.wastage)})</td>
                    </tr>
                    <tr className="border-b border-gray-800 bg-gray-800/30">
                      <td className="py-1.5 text-gray-200 font-medium">Gross Profit</td>
                      <td className="py-1.5 text-right"><ProfitIndicator value={dailyPnl.grossProfit} /></td>
                    </tr>
                    <tr className="border-b border-gray-800">
                      <td className="py-1.5 text-gray-400 pl-4">Less: Overheads (daily)</td>
                      <td className="py-1.5 text-right text-gray-400">({fmt(dailyPnl.overheads.daily)})</td>
                    </tr>
                    <tr className="bg-gray-800/50">
                      <td className="py-2 text-white font-bold">Net Profit</td>
                      <td className="py-2 text-right"><ProfitIndicator value={dailyPnl.netProfit} /></td>
                    </tr>
                  </tbody>
                </table>
                <div className="flex gap-6 pt-2 text-xs text-gray-500">
                  <span>Gross Margin: <strong className="text-gray-300">{fmtPct(dailyPnl.grossMargin)}</strong></span>
                  <span>Net Margin: <strong className="text-gray-300">{fmtPct(dailyPnl.netMargin)}</strong></span>
                  <span>Transactions: <strong className="text-gray-300">{fmtInt(dailyPnl.transactionCount)}</strong></span>
                  <span>Voided: <strong className="text-gray-300">{fmtInt(dailyPnl.voidedCount)}</strong></span>
                </div>
              </div>

              {/* Right: VAT Summary */}
              <div className="bg-gray-900 border border-gray-800 p-5 space-y-3">
                <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider flex items-center gap-2">
                  <DollarSign size={15} className="text-brand-500" />
                  VAT Summary
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Output VAT (Sales)</span>
                    <span className="text-white font-medium">{fmt(dailyPnl.vatSummary.outputVAT)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Input VAT (Purchases)</span>
                    <span className="text-white font-medium">{fmt(dailyPnl.vatSummary.inputVAT)}</span>
                  </div>
                  <div className="border-t border-gray-700 pt-2 flex justify-between text-sm font-bold">
                    <span className="text-gray-200">Net VAT Payable</span>
                    <ProfitIndicator value={dailyPnl.vatSummary.netVAT} />
                  </div>
                </div>
                <div className="pt-4">
                  <div className="text-xs text-gray-500 mb-2">VAT Rate: {(VAT_RATE * 100).toFixed(0)}%</div>
                  <div className="text-xs text-gray-500">Monthly overhead: {fmt(dailyPnl.overheads.monthly)}</div>
                  <div className="text-xs text-gray-500">Trading days/month: {TRADING_DAYS_PER_MONTH}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════ */}
        {/* TAB 2: PERIOD P&L                     */}
        {/* ══════════════════════════════════════ */}
        {tab === 'periodPnl' && (
          <div className="space-y-4">
            {/* Controls */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex gap-1">
                {PERIOD_PRESETS.map(p => (
                  <button
                    key={p.id}
                    onClick={() => setPeriodPreset(p.id)}
                    className={`text-sm px-3 py-1.5 border transition-colors ${
                      periodPreset === p.id
                        ? 'bg-brand-600 border-brand-500 text-white'
                        : 'bg-gray-900 border-gray-700 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              {periodPreset === 'custom' && (
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={customFrom}
                    onChange={e => setCustomFrom(e.target.value)}
                    className="bg-gray-900 border border-gray-700 text-white text-sm px-3 py-1.5 focus:outline-none focus:border-brand-500"
                  />
                  <span className="text-gray-500">to</span>
                  <input
                    type="date"
                    value={customTo}
                    onChange={e => setCustomTo(e.target.value)}
                    className="bg-gray-900 border border-gray-700 text-white text-sm px-3 py-1.5 focus:outline-none focus:border-brand-500"
                  />
                </div>
              )}
              <button
                onClick={handleExportPeriodPnl}
                className="flex items-center gap-1.5 text-sm px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white transition-colors"
              >
                <Download size={14} />
                Export CSV
              </button>
            </div>

            {/* Period Summary */}
            <div className="text-xs text-gray-500">
              Period: {periodPnl.period.from} to {periodPnl.period.to} — {periodPnl.period.days} days ({periodPnl.period.tradingDays} trading days) — {fmtInt(periodPnl.transactions)} transactions
            </div>

            {/* Income Statement */}
            <div className="bg-gray-900 border border-gray-800 p-5 space-y-1">
              <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3 flex items-center gap-2">
                <FileText size={15} className="text-brand-500" />
                Income Statement
              </h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-2 text-gray-400 font-medium">Line Item</th>
                    <th className="text-right py-2 text-gray-400 font-medium w-36">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Revenue */}
                  <tr className="border-b border-gray-800">
                    <td className="py-2 text-gray-200 font-semibold">Revenue</td>
                    <td className="py-2 text-right"></td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-1.5 text-gray-400 pl-4">Sales Revenue (incl VAT)</td>
                    <td className="py-1.5 text-right">{fmt(periodPnl.revenue.total)}</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-1.5 text-gray-400 pl-4">Less: Output VAT</td>
                    <td className="py-1.5 text-right text-gray-400">({fmt(periodPnl.revenue.outputVAT)})</td>
                  </tr>
                  {Object.entries(periodPnl.revenue.byDepartment).map(([dept, amount]) => (
                    <tr key={dept} className="border-b border-gray-800/50">
                      <td className="py-1 text-gray-500 pl-8 text-xs">{dept}</td>
                      <td className="py-1 text-right text-gray-500 text-xs">{fmt(amount)}</td>
                    </tr>
                  ))}
                  <tr className="border-b border-gray-700 bg-gray-800/30">
                    <td className="py-2 text-gray-200 font-semibold">Net Revenue (excl VAT)</td>
                    <td className="py-2 text-right font-semibold">{fmt(periodPnl.revenue.exclusive)}</td>
                  </tr>
                  <tr className="border-b border-gray-800"><td className="py-1" colSpan={2}></td></tr>

                  {/* Cost of Sales */}
                  <tr className="border-b border-gray-800">
                    <td className="py-2 text-gray-200 font-semibold">Less: Cost of Sales</td>
                    <td className="py-2 text-right"></td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-1.5 text-gray-400 pl-4">Cost of Goods Sold</td>
                    <td className="py-1.5 text-right text-gray-400">({fmt(periodPnl.costOfSales.cogs)})</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-1.5 text-gray-400 pl-4">Wastage & Spoilage</td>
                    <td className="py-1.5 text-right text-gray-400">({fmt(periodPnl.costOfSales.wastage)})</td>
                  </tr>
                  <tr className="border-b border-gray-700 bg-gray-800/30">
                    <td className="py-2 text-gray-200 font-semibold">Gross Profit</td>
                    <td className="py-2 text-right">
                      <ProfitIndicator value={periodPnl.grossProfit} label={fmtPct(periodPnl.grossMargin)} />
                    </td>
                  </tr>
                  <tr className="border-b border-gray-800"><td className="py-1" colSpan={2}></td></tr>

                  {/* Overheads */}
                  <tr className="border-b border-gray-800">
                    <td className="py-2 text-gray-200 font-semibold">Less: Operating Expenses</td>
                    <td className="py-2 text-right"></td>
                  </tr>
                  {periodPnl.overheads.byItem.map((o, i) => {
                    const periodAmount = (o.amount / TRADING_DAYS_PER_MONTH) * periodPnl.period.tradingDays
                    return (
                      <tr key={i} className="border-b border-gray-800/50">
                        <td className="py-1.5 text-gray-400 pl-4">
                          {o.name}
                          <span className="text-gray-600 text-xs ml-2">GL {o.glCode}</span>
                        </td>
                        <td className="py-1.5 text-right text-gray-400">({fmt(periodAmount)})</td>
                      </tr>
                    )
                  })}
                  <tr className="border-b border-gray-700">
                    <td className="py-1.5 text-gray-400 pl-4 font-medium">Total Overheads (period)</td>
                    <td className="py-1.5 text-right text-gray-400">({fmt(periodPnl.overheads.period)})</td>
                  </tr>
                  <tr className="border-b border-gray-800"><td className="py-1" colSpan={2}></td></tr>

                  {/* Net Profit */}
                  <tr className="bg-gray-800/50">
                    <td className="py-2.5 text-white font-bold text-base">Net Profit / (Loss)</td>
                    <td className="py-2.5 text-right">
                      <ProfitIndicator value={periodPnl.netProfit} label={fmtPct(periodPnl.netMargin)} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* VAT Return Summary */}
            <div className="bg-gray-900 border border-gray-800 p-5">
              <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Receipt size={15} className="text-brand-500" />
                VAT Return Summary
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Output VAT</div>
                  <div className="text-lg font-bold">{fmt(periodPnl.vatReturn.outputVAT)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Input VAT</div>
                  <div className="text-lg font-bold">{fmt(periodPnl.vatReturn.inputVAT)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Net VAT Payable</div>
                  <div className="text-lg font-bold">
                    <ProfitIndicator value={periodPnl.vatReturn.netPayable} />
                  </div>
                </div>
              </div>
            </div>

            {/* Extra stats */}
            <div className="text-xs text-gray-500">
              Avg daily revenue: {fmt(periodPnl.revenue.avgDaily)} — Total purchases (period): {fmt(periodPnl.purchases.total)}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════ */}
        {/* TAB 3: CASH FLOW                      */}
        {/* ══════════════════════════════════════ */}
        {tab === 'cashFlow' && (
          <div className="space-y-4">
            {/* Controls */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex gap-1">
                {PERIOD_PRESETS.map(p => (
                  <button
                    key={p.id}
                    onClick={() => setCfPreset(p.id)}
                    className={`text-sm px-3 py-1.5 border transition-colors ${
                      cfPreset === p.id
                        ? 'bg-brand-600 border-brand-500 text-white'
                        : 'bg-gray-900 border-gray-700 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              {cfPreset === 'custom' && (
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={cfCustomFrom}
                    onChange={e => setCfCustomFrom(e.target.value)}
                    className="bg-gray-900 border border-gray-700 text-white text-sm px-3 py-1.5 focus:outline-none focus:border-brand-500"
                  />
                  <span className="text-gray-500">to</span>
                  <input
                    type="date"
                    value={cfCustomTo}
                    onChange={e => setCfCustomTo(e.target.value)}
                    className="bg-gray-900 border border-gray-700 text-white text-sm px-3 py-1.5 focus:outline-none focus:border-brand-500"
                  />
                </div>
              )}
            </div>

            {/* Inflows / Outflows / Net */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-gray-900 border border-gray-800 p-4">
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Total Inflows</div>
                <div className="text-xl font-bold text-green-400">{fmt(cashFlow.inflows.total)}</div>
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Cash sales</span>
                    <span className="text-gray-300">{fmt(cashFlow.inflows.cashSales)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Card sales</span>
                    <span className="text-gray-300">{fmt(cashFlow.inflows.cardSales)}</span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-900 border border-gray-800 p-4">
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Total Outflows</div>
                <div className="text-xl font-bold text-red-400">{fmt(cashFlow.outflows.total)}</div>
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Purchases</span>
                    <span className="text-gray-300">{fmt(cashFlow.outflows.purchases)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Overheads</span>
                    <span className="text-gray-300">{fmt(cashFlow.outflows.overheads)}</span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-900 border border-gray-800 p-4">
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Net Cash Flow</div>
                <div className={`text-xl font-bold ${cashFlow.netCashFlow >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {fmt(cashFlow.netCashFlow)}
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  VAT Liability: {fmt(cashFlow.vatLiability)}
                </div>
              </div>
            </div>

            {/* Bar Visualization */}
            <div className="bg-gray-900 border border-gray-800 p-5 space-y-4">
              <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider flex items-center gap-2">
                <BarChart3 size={15} className="text-brand-500" />
                Cash Flow Breakdown
              </h3>
              {(() => {
                const maxVal = Math.max(
                  cashFlow.inflows.cashSales, cashFlow.inflows.cardSales,
                  cashFlow.outflows.purchases, cashFlow.outflows.overheads,
                  1
                )
                return (
                  <div className="space-y-3">
                    <div className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Inflows</div>
                    <SimpleBar label="Cash Sales" value={cashFlow.inflows.cashSales} maxValue={maxVal} color="bg-green-500" />
                    <SimpleBar label="Card Sales" value={cashFlow.inflows.cardSales} maxValue={maxVal} color="bg-green-600" />
                    <div className="border-t border-gray-800 my-2" />
                    <div className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Outflows</div>
                    <SimpleBar label="Purchases" value={cashFlow.outflows.purchases} maxValue={maxVal} color="bg-red-500" />
                    <SimpleBar label="Overheads" value={cashFlow.outflows.overheads} maxValue={maxVal} color="bg-red-600" />
                    <div className="border-t border-gray-800 my-2" />
                    <SimpleBar
                      label="Net Cash Flow"
                      value={cashFlow.netCashFlow}
                      maxValue={maxVal}
                      color={cashFlow.netCashFlow >= 0 ? 'bg-brand-500' : 'bg-red-500'}
                    />
                  </div>
                )
              })()}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════ */}
        {/* TAB 4: TAX COMPLIANCE                 */}
        {/* ══════════════════════════════════════ */}
        {tab === 'tax' && (
          <div className="space-y-4">
            {/* Sub-tabs */}
            <div className="flex gap-1">
              <button
                onClick={() => setTaxSubTab('vat')}
                className={`text-sm px-4 py-2 border transition-colors ${
                  taxSubTab === 'vat'
                    ? 'bg-brand-600 border-brand-500 text-white'
                    : 'bg-gray-900 border-gray-700 text-gray-400 hover:border-gray-500'
                }`}
              >
                VAT201 Prep
              </button>
              <button
                onClick={() => setTaxSubTab('irp6')}
                className={`text-sm px-4 py-2 border transition-colors ${
                  taxSubTab === 'irp6'
                    ? 'bg-brand-600 border-brand-500 text-white'
                    : 'bg-gray-900 border-gray-700 text-gray-400 hover:border-gray-500'
                }`}
              >
                IRP6 Provisional Tax
              </button>
            </div>

            {/* VAT201 */}
            {taxSubTab === 'vat' && (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <select
                    value={selectedVATPeriod}
                    onChange={e => setSelectedVATPeriod(Number(e.target.value))}
                    className="bg-gray-900 border border-gray-700 text-white text-sm px-3 py-1.5 focus:outline-none focus:border-brand-500"
                  >
                    {vatPeriods.map((p, i) => (
                      <option key={i} value={i}>{p.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={handleExportVAT}
                    className="flex items-center gap-1.5 text-sm px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white transition-colors"
                  >
                    <Download size={14} />
                    Export VAT201
                  </button>
                </div>

                {vatData && (
                  <div className="bg-gray-900 border border-gray-800 p-5 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider flex items-center gap-2">
                        <Shield size={15} className="text-brand-500" />
                        SARS VAT201 — {vatData.tradingName}
                      </h3>
                      <span className="text-xs text-gray-500">
                        {vatData.vatPeriod.from} to {vatData.vatPeriod.to}
                      </span>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-700">
                          <th className="text-left py-2 text-gray-400 font-medium w-20">Box</th>
                          <th className="text-left py-2 text-gray-400 font-medium">Description</th>
                          <th className="text-right py-2 text-gray-400 font-medium w-36">Amount (R)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-gray-800">
                          <td className="py-2 text-brand-400 font-medium">Box 1</td>
                          <td className="py-2 text-gray-300">Standard Rated Supplies (excl VAT)</td>
                          <td className="py-2 text-right font-medium">{fmt(vatData.box1_standardRatedSupplies)}</td>
                        </tr>
                        <tr className="border-b border-gray-800">
                          <td className="py-2 text-brand-400 font-medium">Box 1A</td>
                          <td className="py-2 text-gray-300">Output VAT on Supplies</td>
                          <td className="py-2 text-right font-medium">{fmt(vatData.box1a_outputVAT)}</td>
                        </tr>
                        <tr className="border-b border-gray-800">
                          <td className="py-2 text-brand-400 font-medium">Box 14</td>
                          <td className="py-2 text-gray-300">Capital Goods</td>
                          <td className="py-2 text-right font-medium">{fmt(vatData.box14_capitalGoods)}</td>
                        </tr>
                        <tr className="border-b border-gray-800">
                          <td className="py-2 text-brand-400 font-medium">Box 15</td>
                          <td className="py-2 text-gray-300">Input VAT on Acquisitions</td>
                          <td className="py-2 text-right font-medium">{fmt(vatData.box15_inputVAT)}</td>
                        </tr>
                        <tr className="bg-gray-800/50">
                          <td className="py-2.5 text-brand-400 font-bold">Box 18</td>
                          <td className="py-2.5 text-white font-bold">Net VAT Payable / (Refundable)</td>
                          <td className="py-2.5 text-right">
                            <ProfitIndicator value={vatData.box18_netVAT} />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <div className="flex gap-6 text-xs text-gray-500 pt-2">
                      <span>Total sales (incl): {fmt(vatData.totalSalesInclVAT)}</span>
                      <span>Total purchases (incl): {fmt(vatData.totalPurchasesInclVAT)}</span>
                      <span>Transactions: {fmtInt(vatData.transactionCount)}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* IRP6 */}
            {taxSubTab === 'irp6' && (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <select
                    value={selectedTaxYear}
                    onChange={e => setSelectedTaxYear(Number(e.target.value))}
                    className="bg-gray-900 border border-gray-700 text-white text-sm px-3 py-1.5 focus:outline-none focus:border-brand-500"
                  >
                    {taxYears.map((y, i) => (
                      <option key={i} value={i}>Tax Year {y.label}</option>
                    ))}
                  </select>
                </div>

                {irp6Data && (
                  <div className="bg-gray-900 border border-gray-800 p-5 space-y-4">
                    <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider flex items-center gap-2">
                      <Calculator size={15} className="text-brand-500" />
                      IRP6 Provisional Tax Estimate
                    </h3>
                    <table className="w-full text-sm">
                      <tbody>
                        <tr className="border-b border-gray-800">
                          <td className="py-2 text-gray-400">Gross Revenue (excl VAT)</td>
                          <td className="py-2 text-right font-medium">{fmt(irp6Data.grossRevenue)}</td>
                        </tr>
                        <tr className="border-b border-gray-800">
                          <td className="py-2 text-gray-400 pl-4">Less: Cost of Sales</td>
                          <td className="py-2 text-right text-gray-400">({fmt(irp6Data.costOfSales)})</td>
                        </tr>
                        <tr className="border-b border-gray-800 bg-gray-800/30">
                          <td className="py-2 text-gray-200 font-medium">Gross Profit</td>
                          <td className="py-2 text-right font-medium">{fmt(irp6Data.grossProfit)}</td>
                        </tr>
                        <tr className="border-b border-gray-800">
                          <td className="py-2 text-gray-400 pl-4">Less: Operating Expenses</td>
                          <td className="py-2 text-right text-gray-400">({fmt(irp6Data.operatingExpenses)})</td>
                        </tr>
                        <tr className="border-b border-gray-700 bg-gray-800/30">
                          <td className="py-2 text-gray-200 font-semibold">Taxable Income</td>
                          <td className="py-2 text-right font-semibold">{fmt(irp6Data.taxableIncome)}</td>
                        </tr>
                        <tr className="border-b border-gray-800"><td className="py-1" colSpan={2}></td></tr>
                        <tr className="bg-gray-800/50">
                          <td className="py-2.5 text-white font-bold">Estimated Tax</td>
                          <td className="py-2.5 text-right">
                            <span className="text-xl font-bold text-red-400">{fmt(irp6Data.estimatedTax)}</span>
                          </td>
                        </tr>
                        <tr>
                          <td className="py-1.5 text-gray-500 text-xs">Effective Tax Rate</td>
                          <td className="py-1.5 text-right text-gray-400 font-medium">{fmtPct(irp6Data.effectiveRate)}</td>
                        </tr>
                      </tbody>
                    </table>
                    <div className="text-xs text-gray-600 pt-2">
                      Based on SA individual tax brackets (2025/26). This is an estimate only — consult a tax professional.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════ */}
        {/* TAB 5: CHART OF ACCOUNTS              */}
        {/* ══════════════════════════════════════ */}
        {tab === 'coa' && (
          <div className="space-y-4">
            <div className="bg-gray-900 border border-gray-800 p-5">
              <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4 flex items-center gap-2">
                <BookOpen size={15} className="text-brand-500" />
                General Ledger — Chart of Accounts
              </h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-2 text-gray-400 font-medium w-24">Code</th>
                    <th className="text-left py-2 text-gray-400 font-medium">Account Name</th>
                    <th className="text-left py-2 text-gray-400 font-medium w-28">Type</th>
                    <th className="text-left py-2 text-gray-400 font-medium w-28">Category</th>
                  </tr>
                </thead>
                <tbody>
                  {glAccounts.map((acc, i) => {
                    const typeColors = {
                      revenue: 'text-green-400',
                      expense: 'text-red-400',
                    }
                    const catColors = {
                      income: 'bg-green-900/30 text-green-400 border-green-800',
                      contra: 'bg-amber-900/30 text-amber-400 border-amber-800',
                      cogs: 'bg-red-900/30 text-red-400 border-red-800',
                      opex: 'bg-orange-900/30 text-orange-400 border-orange-800',
                    }
                    return (
                      <tr key={acc.code} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                        <td className="py-2 text-brand-400 font-mono font-medium">{acc.code}</td>
                        <td className="py-2 text-gray-200">{acc.name}</td>
                        <td className="py-2">
                          <span className={typeColors[acc.type] || 'text-gray-400'}>{acc.type}</span>
                        </td>
                        <td className="py-2">
                          <span className={`text-[11px] font-medium px-2 py-0.5 border ${catColors[acc.category] || 'border-gray-700 text-gray-400'}`}>
                            {acc.category}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              <div className="text-xs text-gray-600 mt-4">
                {glAccounts.length} accounts — Revenue (4xxx), Cost of Sales (5xxx), Operating Expenses (6xxx)
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════ */}
        {/* AUDIT TRAIL                           */}
        {/* ══════════════════════════════════════ */}
        <div className="bg-gray-900 border border-gray-800 p-5 space-y-3">
          <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider flex items-center gap-2">
            <Shield size={15} className="text-brand-500" />
            Audit Trail
            <span className="text-xs text-gray-600 font-normal ml-2">Last 20 entries</span>
          </h3>
          {auditTrail.length === 0 ? (
            <div className="text-sm text-gray-600 py-4 text-center">No audit trail entries recorded yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-2 text-gray-400 font-medium w-44">Timestamp</th>
                    <th className="text-left py-2 text-gray-400 font-medium">Action</th>
                    <th className="text-left py-2 text-gray-400 font-medium w-32">User</th>
                    <th className="text-left py-2 text-gray-400 font-medium w-40">Entity</th>
                  </tr>
                </thead>
                <tbody>
                  {auditTrail.map((entry, i) => (
                    <tr key={entry.id || i} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                      <td className="py-1.5 text-gray-500 font-mono text-xs">
                        {new Date(entry.timestamp).toLocaleString('en-ZA')}
                      </td>
                      <td className="py-1.5 text-gray-300">{entry.action}</td>
                      <td className="py-1.5 text-gray-400">{entry.user || entry.userId || '—'}</td>
                      <td className="py-1.5 text-gray-500 text-xs truncate max-w-[160px]">
                        {entry.entity || entry.entityType || '—'}
                        {entry.entityId && <span className="ml-1 text-gray-600">#{entry.entityId}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  )
}
